/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

// Window after an ad interaction within which a return to the page still counts
// as an accidental ("back") click. Shared single source of truth for the display
// detector (AccidentalClickDetector) and the advertorial redirect reconcile.
export const BACK_CLICK_WINDOW_MS = 3000;

// Wider, analytics-only window for the /iclk pixel. We record an invalid
// click whenever a bad click's first return happens within an hour, to study
// whether BACK_CLICK_WINDOW_MS is the right window for the reported /aclk signal.
// This is the outer detection ceiling (display detector and advertorial reconcile)
// and the outer threshold for the /iclk decision in the analytics layer.
export const INVALID_CLICK_WINDOW_MS = 3_600_000; // 1 hour

// Window after a heuristic interaction signal (creative click/focus, window
// blur) within which the page must emit navigation evidence (visibilitychange
// -> hidden, or pagehide) for the interaction to be counted. A genuine
// clickthrough opens its destination and always produces one of these; clicks
// on creative UI (close buttons, carousel controls) never do.
// 3000ms (was 1500ms): on mobile, the visibilitychange for the opening tab can
// arrive well after the click — at 1500ms the v1.83.0 rollout dropped ~50% of
// genuine galleryMPU clicks vs GAM. Matches BACK_CLICK_WINDOW_MS, so an
// interaction confirms on the same horizon within which a bounce-back would
// mark it accidental.
export const NAV_EVIDENCE_WINDOW_MS = 3000;

// Minimum candidate-to-evidence latency for PROXIMITY-class triggers (hover
// corroboration on page-spanning surfaces like skin wallpaper rails). A
// genuine click physically cannot produce navigation evidence in ~10ms, but a
// tab-switch made with the cursor parked on a rail fires the blur (candidate)
// and visibilitychange (evidence) as two symptoms of the same action, ~10ms
// apart. Gumtree v1.87.0 data: real clickthroughs confirm at 200-500ms median;
// SkinInteractionTriggerBlurHover false positives cluster at 10-17ms.
export const PROXIMITY_EVIDENCE_MIN_LATENCY_MS = 100;
